import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs';
import { toast } from 'sonner';
import { BILLING_EVENT_STATUSES, INVOICE_STATUSES, type BillingEventRow, type BillingStatus, type InvoiceView, type PlatformPage } from '@barbeiropro/contracts';
import { RotateCw } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { Pill } from '@/features/finance/pill';
import { api, errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { Card, dd, dt, EVENT_LABEL, FilterSelect, IconAction, INVOICE_LABEL, Kpis, useList, usePlatformAbility } from './shared';

function ProviderStatus() {
  const q = useQuery({ queryKey: ['platform', 'billing-status'], queryFn: () => api.get<BillingStatus>('/platform/billing/status') });
  const s = q.data;
  if (!s) return null;
  return (
    <div className="grid gap-4">
      {s.provider === 'mock' && (
        <div role="status" className="rounded-lg border border-brass/40 bg-brass/10 p-3 text-sm">
          Provedor simulado (desenvolvimento): nenhuma cobrança real é gerada. Em produção, configure o Asaas pelas variáveis de ambiente.
        </div>
      )}
      {s.provider === 'none' && (
        <div role="status" className="rounded-lg border border-brass/40 bg-brass/10 p-3 text-sm">
          Cobrança externa desligada: trial, planos e limites funcionam só localmente e não há checkout. Para ligar o Asaas, defina BILLING_PROVIDER=asaas, ASAAS_API_KEY e ASAAS_WEBHOOK_TOKEN.
        </div>
      )}
      <Kpis
        items={[
          { label: 'Provedor', value: s.provider === 'asaas' ? 'Asaas' : s.provider === 'none' ? 'Nenhum' : 'Simulado', hint: s.baseUrl },
          { label: 'Chave da API', value: s.asaasConfigured ? 'Configurada' : 'Ausente', tone: s.asaasConfigured ? undefined : 'warn' },
          { label: 'Token do webhook', value: s.webhookTokenConfigured ? 'Configurado' : 'Ausente', tone: s.webhookTokenConfigured ? undefined : 'warn' },
          { label: 'Eventos com falha', value: String(s.failedEvents), hint: `${s.pendingEvents} pendentes`, tone: s.failedEvents ? 'bad' : undefined },
        ]}
      />
    </div>
  );
}

function EventsTab() {
  const params = useTableParams({ sort: 'receivedAt', order: 'desc' });
  const [status, setStatus] = useQueryState('evStatus', parseAsString);
  const qc = useQueryClient();
  const { canBilling } = usePlatformAbility();
  const query = { ...params.query, status: status ?? undefined };
  const list = useList<PlatformPage<BillingEventRow>>('events', '/platform/billing/events', query);
  const re = useMutation({
    mutationFn: (id: string) => api.post(`/platform/billing/events/${id}/reprocess`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast.success('Evento reprocessado');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const columns: DataColumn<BillingEventRow>[] = [
    { id: 'when', header: 'Recebido', cell: ({ row }) => <span className="tabular whitespace-nowrap">{dt(row.original.receivedAt)}</span> },
    { id: 'type', header: 'Evento', cell: ({ row }) => <code className="text-xs">{row.original.eventType}</code> },
    { id: 'org', header: 'Barbearia', cell: ({ row }) => row.original.organizationName ?? '—' },
    { id: 'status', header: 'Situação', cell: ({ row }) => <Pill tone={row.original.status === 'processed' ? 'good' : row.original.status === 'failed' ? 'bad' : 'neutral'}>{EVENT_LABEL[row.original.status]}</Pill> },
    { id: 'attempts', header: 'Tentativas', meta: { align: 'right' }, cell: ({ row }) => row.original.attempts },
    { id: 'error', header: 'Erro', cell: ({ row }) => <span className="block max-w-xs truncate text-xs text-destructive" title={row.original.error ?? ''}>{row.original.error ?? ''}</span> },
    { id: 'a', header: () => <span className="sr-only">Ações</span>, cell: ({ row }) => canBilling && row.original.status !== 'processed' ? <IconAction label="Reprocessar evento" icon={<RotateCw className="size-4" aria-hidden />} variant="outline" disabled={re.isPending} onClick={() => re.mutate(row.original.id)} /> : null },
  ];
  return (
    <DataTable
      columns={columns}
      data={list.data}
      params={params}
      isLoading={list.isLoading}
      error={list.error}
      onRetry={list.refetch}
      searchPlaceholder="Buscar por tipo ou identificador"
      empty={{ title: 'Nenhum evento recebido', description: 'Eventos de cobrança aparecem aqui assim que o provedor os envia.' }}
      filters={<FilterSelect label="Situação" value={status} onChange={(v) => { void setStatus(v); params.set({ page: 1 }); }} options={BILLING_EVENT_STATUSES.map((s) => ({ value: s, label: EVENT_LABEL[s] }))} />}
    />
  );
}

function InvoicesTab() {
  const params = useTableParams({ sort: 'createdAt', order: 'desc' });
  const [status, setStatus] = useQueryState('invStatus', parseAsString);
  const query = { ...params.query, status: status ?? undefined };
  const list = useList<PlatformPage<InvoiceView>>('invoices', '/platform/billing/invoices', query);
  const columns: DataColumn<InvoiceView>[] = [
    { id: 'org', header: 'Barbearia', cell: ({ row }) => <span className="font-medium">{row.original.organizationName ?? '—'}</span> },
    { id: 'status', header: 'Situação', cell: ({ row }) => <Pill tone={row.original.status === 'paid' ? 'good' : row.original.status === 'overdue' ? 'bad' : 'neutral'}>{INVOICE_LABEL[row.original.status]}</Pill> },
    { id: 'amount', header: 'Valor', meta: { align: 'right' }, cell: ({ row }) => money(row.original.amountCents) },
    { id: 'due', header: 'Vencimento', cell: ({ row }) => dd(row.original.dueDate) },
    { id: 'paid', header: 'Pago em', cell: ({ row }) => dd(row.original.paidAt) },
    { id: 'ext', header: 'Código', cell: ({ row }) => <code className="text-xs">{row.original.externalId}</code> },
  ];
  return (
    <DataTable
      columns={columns}
      data={list.data}
      params={params}
      isLoading={list.isLoading}
      error={list.error}
      onRetry={list.refetch}
      searchPlaceholder="Buscar por barbearia ou código"
      empty={{ title: 'Nenhuma fatura' }}
      filters={<FilterSelect label="Situação" value={status} onChange={(v) => { void setStatus(v); params.set({ page: 1 }); }} options={INVOICE_STATUSES.map((s) => ({ value: s, label: INVOICE_LABEL[s] }))} />}
    />
  );
}

export function BillingPage() {
  const [tab, setTab] = useQueryState('aba', parseAsStringLiteral(['eventos', 'faturas'] as const).withDefault('eventos'));
  return (
    <div className="grid grid-cols-1 gap-6">
      <PageHeader title="Cobrança" description="Espelho do provedor de pagamento. Pagamentos só são confirmados por eventos verificados do provedor; cartões nunca passam por aqui." />
      <ProviderStatus />
      <Card>
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'eventos' | 'faturas')}>
          <TabsList className="mb-4 max-w-full overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="eventos">Eventos</TabsTrigger>
            <TabsTrigger value="faturas">Faturas</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'eventos' ? <EventsTab /> : <InvoicesTab />}
      </Card>
    </div>
  );
}
